import { useEffect, useState } from "react";
import { fetchDisbursementTargets } from "./disbursementApi";
import { money } from "./format";

const reasonText = (reason) => {
  if (reason === "no_service_composition")
    return "Sem composição vinculada a um serviço planejado.";
  if (reason === "composition_missing")
    return "Composição do insumo indisponível.";
  if (reason === "planned_series_missing")
    return "Previsto do serviço indisponível.";
  return "Este insumo ainda não tem dados suficientes para definir uma regra.";
};
const ResourceValue = ({ cents }) => <span>{money(cents)}</span>;
const inheritedLabel = (source) =>
  typeof source === "string" ? source : source?.label ?? source?.key;
const actionLabel = (item) => {
  if (item.ruleId) return "Editar regra";
  return item.inheritedFrom ? "Sobrescrever aqui" : "Definir";
};

const ResourceCatalog = ({
  workId,
  readOnly = false,
  onDefineRule,
  initialClassification = "all",
  reloadKey,
  rulesByRuleId = {},
}) => {
  const [query, setQuery] = useState({
    page: 0,
    pageSize: 50,
    search: "",
    classification: initialClassification,
  });
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setResult(null);
    setError(false);
    fetchDisbursementTargets(workId, { level: "resource", ...query })
      .then((payload) => {
        if (!active) return;
        if (
          !payload ||
          !Array.isArray(payload.items) ||
          !Number.isSafeInteger(payload.total) ||
          payload.total < 0 ||
          payload.page !== query.page ||
          payload.pageSize !== query.pageSize ||
          typeof payload.hasMore !== "boolean"
        ) {
          setError(true);
          return;
        }
        setResult(payload);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workId, query, reloadKey]);
  return (
    <section aria-label="Catálogo de insumos">
      <h3>Catálogo de insumos</h3>
      <p>
        Insumos com e sem regra própria. A busca e a classificação abrangem o
        catálogo inteiro.
      </p>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setQuery((current) => ({
            ...current,
            page: 0,
            search: search.trim(),
          }));
        }}
      >
        <label>
          Buscar insumos
          <input
            aria-label="Buscar insumos"
            maxLength={80}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button type="submit" disabled={loading}>
          Buscar
        </button>
        <label>
          Classificação
          <select
            aria-label="Classificação dos insumos"
            value={query.classification}
            onChange={(event) => {
              const classification = event.target.value;
              setQuery((current) => ({ ...current, classification, page: 0 }));
            }}
          >
            <option value="all">Todos os insumos</option>
            <option value="unclassified">Sem classificação ERP</option>
          </select>
        </label>
      </form>
      {loading ? <p role="status">Carregando insumos…</p> : null}
      {error ? (
        <p role="alert">
          Não foi possível carregar o catálogo completo. Tente buscar novamente.
        </p>
      ) : null}
      {result ? (
        <>
          <p>{`${result.total.toLocaleString("pt-BR")} ${
            result.total === 1 ? "insumo encontrado" : "insumos encontrados"
          } · página ${result.page + 1}`}</p>
          {result.summary ? (
            <p>{`Catálogo da obra: ${
              result.summary.eligible + result.summary.ineligible
            } insumos · ${result.summary.eligible} com previsto disponível · ${
              result.summary.ineligible
            } sem dados suficientes · ${
              result.summary.unclassified
            } sem classificação ERP.`}</p>
          ) : null}
          {result.reason ? (
            <p role="status">
              A fonte está parcial ou indisponível; confira os avisos de cada
              insumo.
            </p>
          ) : null}
          {!result.items.length ? (
            <p>
              {result.reason
                ? "Catálogo indisponível para esta fonte."
                : "Nenhum insumo nesta página ou busca."}
            </p>
          ) : (
            <ul className="ampli-disbursement-list__rows">
              {result.items.map((item) => (
                <li key={item.key} className="ampli-rule-row">
                  <div className="ampli-rule-row__identity">
                    <strong>{item.label}</strong>
                    <span>
                      {item.isClassified
                        ? item.erpGroup
                        : "Sem classificação ERP"}
                    </span>
                    <span>
                      {item.ruleId
                        ? "Regra própria cadastrada"
                        : "Sem regra própria"}
                    </span>
                    {item.inheritedFrom ? (
                      <span>{`Regra herdada: ${inheritedLabel(
                        item.inheritedFrom,
                      )}`}</span>
                    ) : null}
                    {item.isEligible !== true ? (
                      <p>{reasonText(item.reason)}</p>
                    ) : null}
                  </div>
                  <div className="ampli-rule-row__facts">
                    <ResourceValue
                      cents={
                        item.isEligible === true ? item.plannedCents : null
                      }
                    />
                  </div>
                  {!readOnly && item.isEligible === true ? (
                    <button
                      type="button"
                      disabled={Boolean(
                        item.ruleId && !rulesByRuleId[item.ruleId],
                      )}
                      onClick={() => onDefineRule?.(item)}
                    >
                      {actionLabel(item)}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <nav aria-label="Paginação dos insumos">
            <button
              type="button"
              disabled={loading || query.page === 0}
              onClick={() =>
                setQuery((current) => ({ ...current, page: current.page - 1 }))
              }
            >
              Página anterior
            </button>
            <button
              type="button"
              disabled={loading || !result.hasMore}
              onClick={() =>
                setQuery((current) => ({ ...current, page: current.page + 1 }))
              }
            >
              Próxima página
            </button>
          </nav>
        </>
      ) : null}
    </section>
  );
};
export default ResourceCatalog;
